import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { formatImageUrl } from '../../utils/imageUtils';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import GoogleLoginButton from '../../components/GoogleLoginButton';
import AppleLoginButton from '../../components/AppleLoginButton';


const HospitalLogin = () => {
  const navigate = useNavigate();
  const { login, getRedirectPath } = useAuth();
  const { showError } = useNotification();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login('hospital', formData.email, formData.password, formData.rememberMe);
      if (result.success) {
        navigate(getRedirectPath(result.user));
      } else {
        setError(result.message || 'Login failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-dark p-6">
      <div className="text-center mb-8 mt-8">
        <img src={formatImageUrl("/logo.png")} alt="Dr. Vivara Logo" className="w-20 h-20 rounded-full mx-auto mb-4 object-cover border-2 border-gold" />
        <h1 className="text-2xl font-bold text-gold mb-2">Hospital Login</h1>
        <p className="text-light">Sign in to your hospital account</p>
      </div>

      <div className="flex-1">
        {error && (
          <div className="bg-red-900 border border-red-700 rounded-lg p-4 mb-6">
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-light text-sm font-medium mb-2">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full bg-gray-900 rounded-lg px-4 py-3 text-light focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
              placeholder="Enter your email"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-light text-sm font-medium mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full bg-gray-900 rounded-lg px-4 py-3 pr-10 text-light focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gold transition-colors focus:outline-none"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.rememberMe}
                onChange={(e) => setFormData({ ...formData, rememberMe: e.target.checked })}
                className="rounded bg-gray-900 border-gray-700 text-gold focus:ring-gold"
              />
              <span className="ml-2 text-light text-sm">Remember me</span>
            </label>
            <Link to="/forgot-password" className="text-gold text-sm font-medium">
              Forgot Password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gold text-dark font-semibold py-3 px-4 rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <div className="space-y-3">
            <GoogleLoginButton role="hospital" onSuccess={(user) => navigate(getRedirectPath(user))} />
            <AppleLoginButton role="hospital" onSuccess={(user) => navigate(getRedirectPath(user))} />
          </div>

          {/* Compliance Disclaimer */}
          <p className="mt-6 text-center text-xs text-gray-500">
            By signing in, you agree to our{' '}
            <Link to="/terms" className="text-gold hover:underline">Terms & Conditions</Link>
            ,{' '}
            <Link to="/privacy" className="text-gold hover:underline">Privacy Policy</Link>
            , and{' '}
            <Link to="/support" className="text-gold hover:underline">Support</Link>.
          </p>
        </form>
      </div>

      <div className="text-center py-6">
        <p className="text-gray-500 text-sm">
          Don't have an account?{' '}
          <Link to="/auth/hospital/register" className="text-gold font-medium">
            Register as Hospital
          </Link>
        </p>
        <div className="mt-4">
          <Link to="/role-selection" className="text-gray-500 hover:text-gold text-sm transition-colors">
            ← Back to Portal Selection
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HospitalLogin;
